/**
 * A linked Tiller spreadsheet as a second, read-only source of transactions.
 *
 * - Read only: nothing is ever written to the Tiller sheet, and no Tiller row
 *   is written to Finance Buddy Data. Rows are merged in memory on each read,
 *   so unlinking returns the app to exactly what the statements alone give.
 * - Statements win: a Tiller row is dropped when a statement's period covers
 *   that account on that date, or when Finance Buddy already has the same
 *   transaction (same account and amount, within 3 days; a payslip's deposit,
 *   or a purchase a statement dates a day differently). Tiller fills the
 *   history before the first statement and the days since the latest one.
 * - Only accounts already in the Accounts tab take Tiller rows, matched by
 *   last 4 digits. Others are reported, not invented.
 *
 * The link lives in Drive's appDataFolder (tiller.json), apart from the
 * Claude key's settings.json, so clearing one never clears the other.
 */
import { appDataDelete, appDataRead, appDataWrite } from './google/drive'
import { api } from './google/api'
import type { Row } from './google/sheets'
import { parseSheetDate } from './dates'
import { accountKey, brand } from './filing'
import { TILLER_SOURCE } from './duplicates'

const FILE = 'tiller.json'
const SHEETS = 'https://sheets.googleapis.com/v4/spreadsheets'
export { TILLER_SOURCE }

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())

/** The spreadsheet id in a Sheets URL, or the id itself. */
export function spreadsheetIdFrom(input: string): string | undefined {
  const s = input.trim()
  const m = /\/spreadsheets\/d\/([A-Za-z0-9_-]{20,})/.exec(s)
  if (m) return m[1]
  return /^[A-Za-z0-9_-]{20,}$/.test(s) ? s : undefined
}

// --- What Tiller's categories and hints mean here ----------------------------

/** Tiller category (as in this person's Categories tab) → Finance Buddy category. */
const CATEGORY: Record<string, string> = {
  insurance: 'insurance',
  utilities: 'utilities',
  mortgage: 'housing',
  'property taxes': 'taxes',
  'storage unit': 'housing',
  'home repairs & maintenance': 'housing',
  'home services': 'housing',
  'home improvement': 'housing',
  transportation: 'transport',
  groceries: 'groceries',
  medical: 'health',
  fitness: 'health',
  household: 'shopping',
  shopping: 'shopping',
  amazon: 'shopping',
  gifts: 'gifts_donations',
  charity: 'gifts_donations',
  restaurants: 'dining',
  entertainment: 'entertainment',
  'cars - fun': 'entertainment',
  travel: 'travel',
  subscriptions: 'subscriptions',
  'car equipment': 'transport',
  'cars - repairs & maintenance': 'transport',
  'cars - buy/sell': 'transport',
  gas: 'fuel',
  'interest expense': 'interest',
  'interest income': 'interest',
  paycheck: 'income',
  'rewards income': 'income',
  'misc income': 'income',
  'dividend income - other investments': 'investments',
  'dividend income - retirement': 'investments',
  'increase in investments - other investments': 'investments',
  'decrease in investments - other investments': 'investments',
  'increase in investments - retirement': 'investments',
  'decrease in investments - retirement': 'investments',
  'plan fees - retirement investments': 'investments',
}

/** Tiller categories whose rows are one-offs, kept out of the usual monthly figures. */
const ONE_OFF = new Set(['cars - buy/sell'])

/** Tiller's own Category Hint, for the rows nobody categorised in Tiller. */
const HINT: Record<string, string> = {
  transfers: 'transfer',
  'credit card payments': 'transfer',
  'securities trades': 'investments',
  'investment/retirement income': 'investments',
  'retirement contributions': 'investments',
  'electronics/general merchandise': 'shopping',
  'interest income': 'interest',
  'automotive/fuel': 'fuel',
  groceries: 'groceries',
  restaurants: 'dining',
  'service charges/fees': 'fees',
  travel: 'travel',
  'entertainment/recreation': 'entertainment',
  'home improvement': 'housing',
  mortgage: 'housing',
  rent: 'housing',
  'cable/satellite/telecom': 'utilities',
  utilities: 'utilities',
  'healthcare/medical': 'health',
  'other income': 'income',
  'salary/regular income': 'income',
  rewards: 'income',
  taxes: 'taxes',
  'charitable giving': 'gifts_donations',
  'subscriptions/renewals': 'subscriptions',
}

/** Tiller categories that mean "not decided yet": the row is read as if it had none. */
const UNDECIDED = new Set(['', 'questions'])

/**
 * Paying a card off moves money between your own accounts; the purchases are
 * already counted. Only card-payment wording: a bill paid by autopay or
 * e-payment ("VERIZON AUTOPAY", "PGE EPAY") is real spending and must not match.
 */
const CARD_PAYMENT = /cardmember|card ?member|webservpmt|credit ?crd|crd ?pymt|card ?pymt|payment ?-? ?thank you|applecard|amex epayment|elan web pymt/i
/** Money moved to or within a brokerage or TreasuryDirect is investing, not spending. Brand plus investing words, so "Fidelity National Title" doesn't match. */
const INVESTING =
  /m1 finance|fidelity (government|investments|brokerage|money)|(purchase into|redemption from) core|reinvestment|dividend received|vanguard (sell|buy|ebank|bd|index|total|investment|fds)|schwab (brokerage|one|transfer|moneylink)|robinhood|wealthfront|betterment|treasury ?direct|treas drct/i
/** An undecided row this large is kept out of the usual monthly figures until someone says what it was. */
const LARGE_UNKNOWN = 5000

/**
 * The Finance Buddy category for a Tiller row, and whether it's a one-off.
 * A category set in Tiller wins, by name or by its Type in the Categories tab.
 * An undecided row (no category, or Questions) is read from its description,
 * then Tiller's hint, else other; a large other is a one-off.
 */
export function categoryFor(row: { category: string; hint: string; description: string; amount: number }, types: Map<string, string>): { category: string; oneOff: boolean } {
  const c = row.category.toLowerCase()
  if (!UNDECIDED.has(c)) {
    if (Object.hasOwn(CATEGORY, c)) return { category: CATEGORY[c], oneOff: ONE_OFF.has(c) }
    const type = types.get(c)
    return { category: type === 'transfer' ? 'transfer' : type === 'income' ? 'income' : 'other', oneOff: false }
  }
  if (CARD_PAYMENT.test(row.description)) return { category: 'transfer', oneOff: false }
  if (INVESTING.test(row.description)) return { category: 'investments', oneOff: false }
  const h = row.hint.toLowerCase()
  const category = Object.hasOwn(HINT, h) ? HINT[h] : 'other'
  return { category, oneOff: category === 'other' && row.amount <= -LARGE_UNKNOWN }
}

// --- Reading the sheet -------------------------------------------------------

export interface TillerSheet {
  spreadsheetId: string
  title: string
  /** Transactions tab rows keyed by header. */
  rows: Row[]
  /** Category (lower case) → its Type in the Categories tab (lower case). */
  types: Map<string, string>
}

async function values(spreadsheetId: string, range: string): Promise<Row[]> {
  // Serial-number dates: parseSheetDate turns them into YYYY-MM-DD whatever the sheet's locale.
  const params = 'valueRenderOption=UNFORMATTED_VALUE&dateTimeRenderOption=SERIAL_NUMBER'
  const res = await api.get<{ values?: unknown[][] }>(`${SHEETS}/${spreadsheetId}/values/${encodeURIComponent(range)}?${params}`)
  const [header = [], ...body] = res.values ?? []
  const names = header.map((h) => str(h))
  return body.map((r) => Object.fromEntries(names.map((n, i) => [n, (r[i] ?? null) as Row[string]])))
}

export async function readTillerSheet(spreadsheetId: string): Promise<TillerSheet> {
  const meta = await api.get<{ properties: { title: string }; sheets: { properties: { title: string } }[] }>(
    `${SHEETS}/${spreadsheetId}?fields=properties.title,sheets.properties.title`,
  )
  const tabs = meta.sheets.map((s) => s.properties.title)
  if (!tabs.includes('Transactions')) throw new Error(`“${meta.properties.title}” has no Transactions tab, so it doesn’t look like a Tiller sheet`)
  const [rows, categories] = await Promise.all([
    values(spreadsheetId, "'Transactions'!A1:AZ"),
    tabs.includes('Categories') ? values(spreadsheetId, "'Categories'!A1:D") : Promise.resolve([]),
  ])
  const types = new Map(categories.map((c) => [str(c.Category).toLowerCase(), str(c.Type).toLowerCase()]))
  return { spreadsheetId, title: meta.properties.title, rows, types }
}

// --- Merging -----------------------------------------------------------------

export interface TillerReport {
  title: string
  read: number
  used: number
  /** Dropped because a statement covers that account on that date. */
  covered: number
  /** Dropped because Finance Buddy already has it: same account and amount within 3 days. */
  matched: number
  /** Accounts with no match in the Accounts tab, and how many rows each had. */
  unmatched: { account: string; rows: number }[]
  /** Rows without a usable date or amount. */
  unreadable: number
}

const last4Of = (s: string) => {
  const m = /([A-Za-z0-9]{4})\s*$/.exec(s)
  return m ? m[1].toLowerCase() : ''
}
const words = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ')

/**
 * The Accounts row a Tiller account is: the same last 4, and the same bank.
 * The bank matches by brand ("Fidelity Investments" is "Fidelity"), or when
 * Finance Buddy's bank name appears in Tiller's account name ("Amazon Business
 * Prime Card" for an Amazon card Tiller files under American Express). A last
 * 4 alone could be a card at another bank, so that stays unmatched.
 */
function matcher(accounts: Row[]) {
  return (number: string, institution: string, name: string): string | undefined => {
    const l4 = last4Of(number)
    if (!l4) return undefined
    const theirs = ` ${words(`${institution} ${name}`)} `
    const fits = accounts.filter((a) => {
      if (str(a.last4).toLowerCase() !== l4) return false
      const b = brand(str(a.institution))
      return !!b && (b === brand(institution) || theirs.includes(` ${b} `))
    })
    return fits.length === 1 ? str(fits[0].account_id) : undefined
  }
}

/** Documents that list every transaction on their accounts for a period. A payslip or receipt doesn't. */
const STATEMENTS = new Set(['bank_statement', 'credit_card_statement', 'brokerage_statement', 'retirement_statement', 'loan_statement'])

/**
 * The dates each account's statements cover: each statement's period, or its
 * first to last transaction where it gives no period. Only statements count;
 * a payslip's single deposit says nothing about the rest of that account.
 */
export function statementCoverage(transactions: Row[], documents: Row[]): Map<string, [string, string][]> {
  const period = new Map(documents.filter((d) => STATEMENTS.has(str(d.doc_type))).map((d) => [str(d.file_id), d]))
  const spans = new Map<string, [string, string]>()
  for (const t of transactions) {
    const source = str(t.source_file_id)
    const date = parseSheetDate(t.date)
    if (!period.has(source) || !date) continue
    const k = `${source}|${accountKey(str(t.account_id))}`
    const [from, to] = spans.get(k) ?? [date, date]
    spans.set(k, [date < from ? date : from, date > to ? date : to])
  }
  const out = new Map<string, [string, string][]>()
  for (const [k, [from, to]] of spans) {
    const [source, account] = k.split('|')
    const d = period.get(source)
    const start = parseSheetDate(d?.period_start)
    const end = parseSheetDate(d?.period_end)
    // The period when the statement gives one: transactions it dates outside it are matched one by one instead.
    out.set(account, [...(out.get(account) ?? []), start && end ? [start, end] : [from, to]])
  }
  return out
}

const DAY = 86_400_000
const MATCH_DAYS = 3

/**
 * Whether Finance Buddy already has a transaction: same account, same amount
 * to the cent, dated within 3 days. Each of its rows answers for one Tiller
 * row only, so two real $3.90 purchases aren't merged into one.
 */
function existing(transactions: Row[]) {
  const byKey = new Map<string, { at: number; used: boolean }[]>()
  for (const t of transactions) {
    const date = parseSheetDate(t.date)
    const amount = Number(t.amount)
    if (!date || !Number.isFinite(amount)) continue
    const k = `${accountKey(str(t.account_id))}|${Math.round(amount * 100)}`
    byKey.set(k, [...(byKey.get(k) ?? []), { at: Date.parse(date), used: false }])
  }
  /** Takes the closest unused match within `days`; 0 for the same day only. */
  return (account: string, amount: number, date: string, days: number): boolean => {
    const at = Date.parse(date)
    const hit = (byKey.get(`${accountKey(account)}|${Math.round(amount * 100)}`) ?? [])
      .filter((c) => !c.used && Math.abs(c.at - at) <= days * DAY)
      .sort((a, b) => Math.abs(a.at - at) - Math.abs(b.at - at))[0]
    if (hit) hit.used = true
    return !!hit
  }
}

/**
 * Tiller rows in Finance Buddy's Transactions shape, minus those a statement
 * covers and those on accounts Finance Buddy doesn't have.
 */
export function tillerTransactions(sheet: TillerSheet, tables: { accounts: Row[]; transactions: Row[]; documents: Row[] }): { rows: Row[]; report: TillerReport } {
  const accountFor = matcher(tables.accounts)
  const covered = statementCoverage(tables.transactions, tables.documents)
  const isCovered = (account: string, date: string) => (covered.get(accountKey(account)) ?? []).some(([from, to]) => date >= from && date <= to)
  const unmatched = new Map<string, number>()
  const report: TillerReport = { title: sheet.title, read: 0, used: 0, covered: 0, matched: 0, unmatched: [], unreadable: 0 }
  const already = existing(tables.transactions)
  const candidates: { r: Row; account: string; date: string; amount: number; n: number }[] = []
  for (const r of sheet.rows) {
    const date = parseSheetDate(r.Date)
    const amount = typeof r.Amount === 'number' ? r.Amount : str(r.Amount) ? Number(str(r.Amount).replace(/[$,\s]/g, '')) : NaN
    if (!str(r.Date) && !str(r.Amount) && !str(r.Description)) continue
    report.read++
    if (!date || !Number.isFinite(amount)) {
      report.unreadable++
      continue
    }
    const name = str(r.Account) || str(r['Account #'])
    const account = accountFor(str(r['Account #']), str(r.Institution), str(r.Account))
    if (!account) {
      unmatched.set(name, (unmatched.get(name) ?? 0) + 1)
      continue
    }
    if (isCovered(account, date)) {
      report.covered++
      continue
    }
    candidates.push({ r, account, date, amount, n: report.read })
  }
  // Same-day matches are taken first, across all rows, so a near match can't
  // use up the Finance Buddy row that another Tiller row matches exactly.
  const kept = candidates.filter((c) => !already(c.account, c.amount, c.date, 0)).filter((c) => !already(c.account, c.amount, c.date, MATCH_DAYS))
  report.matched = candidates.length - kept.length
  const rows: Row[] = []
  for (const { r, account, date, amount, n } of kept) {
    const mapped = categoryFor(
      { category: str(r.Category), hint: str(r['Category Hint']), description: `${str(r.Description)} ${str(r['Full Description'])}`, amount },
      sheet.types,
    )
    rows.push({
      txn_id: `tiller:${str(r['Transaction ID']) || `${account}:${date}:${amount}:${n}`}`,
      account_id: account,
      date,
      description: str(r['Full Description']) || str(r.Description),
      merchant: str(r.Payee) || str(r.Description),
      amount,
      category: mapped.category,
      source_file_id: TILLER_SOURCE,
      ...(mapped.oneOff ? { one_off: 'yes' } : {}),
    })
    report.used++
  }
  report.unmatched = [...unmatched].map(([account, n]) => ({ account, rows: n })).sort((a, b) => b.rows - a.rows)
  return { rows, report }
}

// --- The link and the per-page cache -----------------------------------------

let cached: Promise<TillerSheet | null> | undefined
let lastError: string | undefined
let lastReport: TillerReport | undefined

async function linkedId(): Promise<string | undefined> {
  const text = await appDataRead(FILE)
  if (!text) return undefined
  try {
    return (JSON.parse(text) as { spreadsheetId?: string }).spreadsheetId || undefined
  } catch {
    return undefined
  }
}

/**
 * The linked sheet, read once per page load; null when none is linked or it
 * can't be read. A failure is never kept: the next read tries again, and it
 * never stops the Sheet's own figures from loading.
 */
function sheet(): Promise<TillerSheet | null> {
  cached ??= (async () => {
    try {
      const id = await linkedId()
      lastError = undefined
      if (!id) return null
      return await readTillerSheet(id)
    } catch (e) {
      lastError = (e as Error).message
      cached = undefined
      return null
    }
  })()
  return cached
}

export const tiller = {
  linkedId,
  async link(input: string): Promise<TillerSheet> {
    const id = spreadsheetIdFrom(input)
    if (!id) throw new Error('That isn’t a Google Sheets link')
    const read = await readTillerSheet(id)
    await appDataWrite(FILE, JSON.stringify({ spreadsheetId: id }))
    cached = Promise.resolve(read)
    lastError = undefined
    return read
  },
  async unlink(): Promise<void> {
    await appDataDelete(FILE)
    cached = Promise.resolve(null)
    lastError = undefined
    lastReport = undefined
  },
  /** Forgets everything read, for the next account to sign in on this page. */
  reset(): void {
    cached = undefined
    lastError = undefined
    lastReport = undefined
  },
  /** Why the linked sheet couldn't be read this page load, if it couldn't. */
  error: () => lastError,
  /** What the last merge used and left out. */
  report: () => lastReport,
  /**
   * The tables with Tiller rows added when a sheet is linked; the same tables,
   * untouched, when none is or it can't be read.
   */
  async merge<T extends { accounts: Row[]; transactions: Row[]; documents: Row[] }>(tables: T): Promise<T> {
    const s = await sheet()
    if (!s) return tables
    const { rows, report } = tillerTransactions(s, tables)
    lastReport = report
    return { ...tables, transactions: [...tables.transactions, ...rows] }
  },
}
